import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import type { SmartScoreSummary } from '../../../services/gamification.service';
import {
  DIMENSION_ORDER,
  DIMENSIONS,
  DimensionMeta,
  PointDimension,
  progressPercent,
  remainingDailyCap
} from '../../../shared/gamification/smart-score.utils';

interface DailyRow {
  key: 'vitalis' | 'pulse';
  meta: DimensionMeta;
  earned: number;
  cap: number;
  remaining: number;
  percent: number;
}

@Component({
  selector: 'app-smart-score-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './smart-score-modal.component.html',
  styleUrls: ['./smart-score-modal.component.css']
})
export class SmartScoreModalComponent {
  @Input() summary: SmartScoreSummary | null = null;
  @Output() close = new EventEmitter<void>();

  readonly dimensionOrder = DIMENSION_ORDER;
  readonly dimensions = DIMENSIONS;

  get dailyRows(): DailyRow[] {
    const s = this.summary;
    if (!s) {
      return [];
    }
    return [
      this.buildRow('vitalis', s.daily.vitalisToday, s.daily.vitalisCap),
      this.buildRow('pulse', s.daily.pulseToday, s.daily.pulseCap)
    ];
  }

  get karmaPercent(): number {
    const k = this.summary?.karma;
    return k ? progressPercent(k.givenThisMonth, k.monthlyAllowance) : 0;
  }

  get karmaRemaining(): number {
    const k = this.summary?.karma;
    return k ? remainingDailyCap(k.givenThisMonth, k.monthlyAllowance) : 0;
  }

  valueFor(key: PointDimension): number {
    const s = this.summary;
    if (!s) {
      return 0;
    }
    if (key === 'vitalis') {
      return s.vitalis;
    }
    if (key === 'pulse') {
      return s.pulse;
    }
    return s.karmaReceived;
  }

  private buildRow(key: 'vitalis' | 'pulse', earned: number, cap: number): DailyRow {
    return {
      key,
      meta: DIMENSIONS[key],
      earned,
      cap,
      remaining: remainingDailyCap(earned, cap),
      percent: progressPercent(earned, cap)
    };
  }
}
